/**
 * NotificationsContext — Merkezi bildirim veri sağlayıcısı.
 * Daha önce iki NotificationBell (desktop + mobile) kendi başlarına polling yapıyordu,
 * bu /notifications çağrısını iki katına çıkarıyordu. Artık tek yerde yönetiliyor.
 *
 * - 60s'de bir arka plan polling (eskiden 30s'di)
 * - `rt:notification` WS event'inde anlık yenileme (yeni bildirim gelince)
 * - İki bell komponenti aynı state'i okur
 */
import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const NotificationsContext = createContext({
  unread_count: 0,
  items: [],
  reload: () => {},
  markOne: () => {},
  markAll: () => {},
  delOne: () => {},
});

export function NotificationsProvider({ children }) {
  const { user } = useAuth();
  const [data, setData] = useState({ unread_count: 0, items: [] });
  const inflight = useRef(false);

  const reload = useCallback(async () => {
    if (inflight.current) return;
    inflight.current = true;
    try {
      const r = await api.get("/notifications", { params: { limit: 30 } });
      setData(r.data);
    } catch (e) {}
    finally {
      inflight.current = false;
    }
  }, []);

  const markOne = useCallback(async (nid) => {
    try { await api.post(`/notifications/${nid}/read`); } catch {}
    reload();
  }, [reload]);

  const markAll = useCallback(async () => {
    try { await api.post("/notifications/read-all"); } catch {}
    reload();
  }, [reload]);

  const delOne = useCallback(async (nid) => {
    try { await api.delete(`/notifications/${nid}`); } catch {}
    reload();
  }, [reload]);

  // Oturum açılınca bir kez yükle + 60s polling
  useEffect(() => {
    if (!user) {
      setData({ unread_count: 0, items: [] });
      return;
    }
    reload();
    const t = setInterval(reload, 60000);
    // WS'den "notification" invalidate gelirse anlık yenile
    const onRt = () => reload();
    window.addEventListener("rt:notification", onRt);
    return () => {
      clearInterval(t);
      window.removeEventListener("rt:notification", onRt);
    };
  }, [user, reload]);

  const value = { ...data, reload, markOne, markAll, delOne };
  return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
}

export function useNotifications() {
  return useContext(NotificationsContext);
}
